import { searchRecords } from "../../../api";
import {
  CdcmSearchResultObj,
  EvaluatedResults,
  GenericSearchResponseObject,
  IAMUserState,
  ISelectableOption,
  JsonSchema,
  JsonSchemaProperty,
  PrimitiveValueType,
  RecordsSearchFilterRequest,
  RowData,
  RowDataObject,
  RowDataValueType,
  SetupBaseRequestInfo,
  SetupListNavInfoByDepartment,
  EnumLabels,
} from "../../../types";
import {
  ENUM_DISPLAY_MAPPINGS,
  EVALUATED_RESULT_KEYS,
  ROW_DATA_KEYS,
  SCHEMA_TITLES,
} from "../constants";
import {
  getPurchaseOrderAuthorities,
  getRecordsBySearchFilter,
  getRowDataList,
} from "./dataHelpers";
import { getGLGroupIdOptions, getSelectedGroupIdOption } from "./group-id";
import { convertToOptions } from "./optionHelpers";
import { isNewRecord } from "./rowDataHelpers";
import { getDepartmentIdByStore } from "./storeHelpers";

export const setEvaluationResultsIfNeeded = async ({
  setupBaseRequestInfo,
  evaluatedResults,
  setEvaluatedResults,
  iamUserState,
  setupListNavInfoByDepartment,
}: {
  setupBaseRequestInfo: SetupBaseRequestInfo;
  evaluatedResults: EvaluatedResults;
  setEvaluatedResults: (evaluatedResults: EvaluatedResults) => void;
  iamUserState: IAMUserState;
  setupListNavInfoByDepartment: SetupListNavInfoByDepartment;
}): Promise<void> => {
  const {
    storeId,
    enterpriseId,
    functionalArea,
    functionalUnitName,
    accountName,
    groupId,
  } = setupBaseRequestInfo;

  const setupsWhichRequirePOAuthorities = [SCHEMA_TITLES.PO_ROLE_LEVEL_INFO];
  const setupsWhichRequiredFormIdAndProvisionIdOptions = [
    SCHEMA_TITLES.REMITTANCE_FORM_MAPPING,
  ];
  const setupsWhichRequiresInventoryRoutes = [
    SCHEMA_TITLES.INVENTORY_BUILDINGS,
  ];
  const setupsWhichRequiresLaborTypeOptions = [
    SCHEMA_TITLES.DEFAULT_LABOR_TYPES,
  ];
  const setupsWhichRequiresIntercoCompanyOptions = [
    SCHEMA_TITLES.CASH_CLEARING_SETUP,
  ];

  if (setupsWhichRequirePOAuthorities.includes(functionalUnitName)) {
    if (evaluatedResults?.[EVALUATED_RESULT_KEYS.PO_AUTHORITIES]) return;
    const response = await getPurchaseOrderAuthorities();
    setEvaluatedResults({
      ...evaluatedResults,
      [EVALUATED_RESULT_KEYS.PO_AUTHORITIES]: response,
    });
  }

  if (
    setupsWhichRequiredFormIdAndProvisionIdOptions.includes(functionalUnitName)
  ) {
    const REQUEST: RecordsSearchFilterRequest = {
      ...setupBaseRequestInfo,
      url: "/RemittanceFormMapping",
      body: {
        searchKey: "FORM_PROVISION",
        filters: [],
        responseFields: [],
        department: getDepartmentIdByStore({
          store: setupBaseRequestInfo.storeId,
          department: setupBaseRequestInfo.functionalArea,
          extendedAccountInfo: iamUserState.extendedAccountInfo,
          setupListNavInfoByDepartment,
        }),
      },
      valueField: "",
      labelField: [],
    };

    const response = await searchRecords(REQUEST);

    setEvaluatedResults({
      ...evaluatedResults,
      [EVALUATED_RESULT_KEYS.FORM_ID_AND_PROVISION_ID_OPTIONS]:
        response.resultObj as CdcmSearchResultObj,
    });
  }

  if (setupsWhichRequiresInventoryRoutes.includes(functionalUnitName)) {
    const updatedSetupBaseRequestInfo = {
      ...setupBaseRequestInfo,
      functionalUnitName: SCHEMA_TITLES.INVENTORY_ROUTES,
    };
    const response = await getRowDataList(updatedSetupBaseRequestInfo);

    const routeNameOptions: ISelectableOption[] = response.map(
      (route: RowData) => ({
        label: route[ROW_DATA_KEYS.ROUTE_NAME] as string,
        value: route[ROW_DATA_KEYS.ROUTE_NAME] as string,
      }),
    );

    setEvaluatedResults({
      ...evaluatedResults,
      [EVALUATED_RESULT_KEYS.INVENTORY_ROUTES]: routeNameOptions,
    });
  }

  if (setupsWhichRequiresLaborTypeOptions.includes(functionalUnitName)) {
    const updatedSetupBaseRequestInfo = {
      ...setupBaseRequestInfo,
      functionalUnitName: SCHEMA_TITLES.LABOR_TYPES,
    };
    const response = await getRowDataList(updatedSetupBaseRequestInfo);

    const laborTypeOptions: ISelectableOption[] = response.map(
      (laborType: RowData) => ({
        label: `${laborType[ROW_DATA_KEYS.LABOR_TYPE]} - ${laborType[ROW_DATA_KEYS.LABOR_TYPE_DESCRIPTION]}`,
        value: `${laborType[ROW_DATA_KEYS.LABOR_TYPE]}`,
      }),
    );

    const customerPayLaborTypeOptions = laborTypeOptions.filter((option) =>
      (option.value as string).toUpperCase().startsWith("C"),
    );
    customerPayLaborTypeOptions.push({ label: "None", value: null });

    const warrantyPayLaborTypeOptions = laborTypeOptions.filter((option) =>
      (option.value as string).toUpperCase().startsWith("W"),
    );
    warrantyPayLaborTypeOptions.push({ label: "None", value: null });

    const internalPayLaborTypeOptions = laborTypeOptions.filter((option) =>
      (option.value as string).toUpperCase().startsWith("I"),
    );
    internalPayLaborTypeOptions.push({ label: "None", value: null });

    setEvaluatedResults({
      ...evaluatedResults,
      [EVALUATED_RESULT_KEYS.DEFAULT_LABOR_TYPE_OPTIONS]: laborTypeOptions,
      [EVALUATED_RESULT_KEYS.CUSTOMER_PAY_TYPE_OPTIONS]:
        customerPayLaborTypeOptions,
      [EVALUATED_RESULT_KEYS.WARRANTY_PAY_TYPE_OPTIONS]:
        warrantyPayLaborTypeOptions,
      [EVALUATED_RESULT_KEYS.INTERNAL_PAY_TYPE_OPTIONS]:
        internalPayLaborTypeOptions,
    });
  }

  if (setupsWhichRequiresIntercoCompanyOptions.includes(functionalUnitName)) {
    const request: RecordsSearchFilterRequest = {
      storeId,
      enterpriseId,
      functionalArea,
      functionalUnitName,
      accountName,
      groupId,
      departmentId: setupBaseRequestInfo.departmentId,
      url: "/v2/search/IntercoTransfer",
      body: {
        filters: [
          {
            field: ROW_DATA_KEYS.SOURCE_COMPANY_GROUP_ID,
            operator: "EQUALS",
            value: `${setupBaseRequestInfo.groupId}`,
          },
        ],
        responseFields: ["target_company_group_id"],
      },
      valueField: "target_company_group_id",
      labelField: ["target_company_group_id"],
    };

    const { options } = await getRecordsBySearchFilter(request);

    const companyGroupIds = options.map((option) => option.value as string);

    const companyGroupIdOptions = getGLGroupIdOptions({
      groupIds: companyGroupIds,
      selectedGroupId: setupBaseRequestInfo.groupId as string,
      eisGroups: iamUserState.eisGroups,
    });

    setEvaluatedResults({
      ...evaluatedResults,
      [EVALUATED_RESULT_KEYS.INTERCO_COMPANY_GROUP_ID_OPTIONS]:
        companyGroupIdOptions,
    });
  }
};

export const getEvaluatedPOAuthoritiesOptions = (
  evaluatedResults: EvaluatedResults | undefined,
): ISelectableOption[] => {
  const poAuthorities =
    (evaluatedResults?.[EVALUATED_RESULT_KEYS.PO_AUTHORITIES] as string[]) ??
    [];

  const filteredPOAuthorities = poAuthorities.filter((authority: string) => {
    return authority.startsWith("APPROVER_PO_");
  });
  return convertToOptions(filteredPOAuthorities);
};

export const getRemittanceFormMappingFormIdOptions = (
  evaluatedResults: EvaluatedResults | undefined,
  rowData: RowData,
): ISelectableOption[] => {
  const cashStoreId = rowData[ROW_DATA_KEYS.CASH_STORE_ID];

  if (!cashStoreId)
    return [{ label: "Select Cash Store", value: "", disabled: true }];

  const formIdsData =
    (
      evaluatedResults?.[
        EVALUATED_RESULT_KEYS.FORM_ID_AND_PROVISION_ID_OPTIONS
      ] as CdcmSearchResultObj
    )?.data ?? [];

  const formIdsDataForCashStore = formIdsData.filter(
    ({ store_id }) => store_id === cashStoreId,
  );

  if (formIdsDataForCashStore.length === 0) {
    return [
      { label: "No Form IDs For Cash Store", value: null, disabled: true },
    ];
  }

  return formIdsDataForCashStore.map(({ formId, formNumber, formName }) => ({
    label: String(formNumber) + " - " + String(formName),
    value: String(formId),
  }));
};

export const getRemittanceFormMappingProvisionIdOptions = (
  evaluatedResults: EvaluatedResults | undefined,
  rowData: RowData,
  rowIndex?: number,
): ISelectableOption[] => {
  const selectedCashStore = rowData[ROW_DATA_KEYS.CASH_STORE_ID];
  const selectedFormId =
    (rowIndex !== undefined
      ? (rowData.forms as RowDataObject[])?.[rowIndex]?.[ROW_DATA_KEYS.FORM_ID]
      : undefined) ??
    rowData[ROW_DATA_KEYS.FORM_ID] ??
    "";

  const formData =
    (
      evaluatedResults?.[
        EVALUATED_RESULT_KEYS.FORM_ID_AND_PROVISION_ID_OPTIONS
      ] as CdcmSearchResultObj
    )?.data ?? [];

  const selectedForm = formData.find(
    ({ store_id, formId }) =>
      store_id === selectedCashStore && formId === selectedFormId,
  );

  if (!selectedForm)
    return [{ label: "Select Form Id", value: "", disabled: true }];

  const provisions =
    (selectedForm.provisions as GenericSearchResponseObject[]) ?? [];

  if (provisions.length === 0)
    return [{ label: "No Provisions", value: null, disabled: true }];

  const selectedProvisionIds = new Set(
    ((rowData.forms as RowDataObject[]) ?? [])
      .filter((_, index) => index !== rowIndex)
      .map((form) => String(form[ROW_DATA_KEYS.PROVISION_ID]))
      .filter(Boolean),
  );

  const options = provisions
    .filter(({ provisionId }) => !selectedProvisionIds.has(String(provisionId)))
    .map(({ provisionId, aliasName }) => ({
      label: `${aliasName} - ${provisionId}`,
      value: String(provisionId),
    }));

  return options.length > 0
    ? options
    : [{ label: "No results", value: null, disabled: true }];
};

export const getFormIdOrProvisionIdLabel = (
  fieldName: string,
  fieldValue: string,
  evaluatedResults: EvaluatedResults,
  rowData: RowData,
): string => {
  if (fieldName === ROW_DATA_KEYS.FORM_ID) {
    return (
      getRemittanceFormMappingFormIdOptions(evaluatedResults, rowData).find(
        (option) => option.value === fieldValue,
      )?.label ?? fieldValue
    );
  }

  if (fieldName === ROW_DATA_KEYS.PROVISION_ID) {
    const currentRow = rowData;
    const provisionRowData: RowData = {
      [ROW_DATA_KEYS.FORM_ID]: currentRow[ROW_DATA_KEYS.FORM_ID],
      [ROW_DATA_KEYS.CASH_STORE_ID]: currentRow[ROW_DATA_KEYS.CASH_STORE_ID],
    };

    return (
      getRemittanceFormMappingProvisionIdOptions(
        evaluatedResults,
        provisionRowData,
      ).find((option) => option.value === fieldValue)?.label ?? fieldValue
    );
  }

  return fieldValue;
};

export const getInventoryBuildingsRouteOptions = (
  evaluatedResults: EvaluatedResults | undefined,
): ISelectableOption[] => {
  const routeNameOptions =
    (evaluatedResults?.[
      EVALUATED_RESULT_KEYS.INVENTORY_ROUTES
    ] as ISelectableOption[]) ?? [];

  return routeNameOptions.sort((a, b) => a.label.localeCompare(b.label));
};

// TODO: This helper might be removed once enumLabels are consistently used in the schema.
const getEnumDisplayMappings = (
  setupKey: string,
  propertyKey: string,
): Record<string, string> => {
  return ENUM_DISPLAY_MAPPINGS?.[setupKey]?.[propertyKey] ?? {};
};

export const getEnumDisplayMappingOptions = (
  setupKey: string,
  propertyKey: string,
  enumValues?: PrimitiveValueType[],
  enumLabels?: EnumLabels,
): ISelectableOption[] => {
  if (!enumValues?.length) {
    return [];
  }

  const displayMappings = getEnumDisplayMappings(setupKey, propertyKey);

  return enumValues.map((value) => ({
    value,
    label:
      enumLabels?.[String(value)] ??
      displayMappings?.[String(value)] ??
      (value === null ? "None" : String(value)),
  }));
};

export const getLabelForEnumValue = ({
  setupKey,
  fieldName,
  fieldValue,
  property,
}: {
  setupKey: string;
  fieldName: string;
  fieldValue: RowDataValueType;
  property?: JsonSchemaProperty;
}): RowDataValueType => {
  const displayMappings = getEnumDisplayMappings(setupKey, fieldName);

  return (
    property?.enumLabels?.[String(fieldValue)] ??
    displayMappings?.[String(fieldValue)] ??
    fieldValue
  );
};

/**
 * Resolves the display label for Cash Clearing Setup fields that require
 * schema-driven enum labels or group-id lookups; returns undefined for any
 * other field so callers can fall back to their default formatting.
 */
export const getCashClearingSetupFieldLabel = ({
  setupName,
  fieldName,
  fieldValue,
  schema,
  iamUserState,
}: {
  setupName: string;
  fieldName: string;
  fieldValue: RowDataValueType;
  schema: JsonSchema;
  iamUserState: IAMUserState;
}): RowDataValueType | undefined => {
  if (setupName !== SCHEMA_TITLES.CASH_CLEARING_SETUP) return undefined;

  if (
    fieldName === ROW_DATA_KEYS.INTEGRATING_APPLICATION ||
    fieldName === ROW_DATA_KEYS.TRANSACTION_TYPE
  ) {
    return getLabelForEnumValue({
      setupKey: setupName,
      fieldName,
      fieldValue,
      property: schema.properties?.[fieldName],
    });
  }

  if (
    fieldName === ROW_DATA_KEYS.CASH_CLEARING_COMPANY_GROUP_ID ||
    fieldName === ROW_DATA_KEYS.CASH_IN_BANK_COMPANY_GROUP_ID
  ) {
    return getSelectedGroupIdOption(
      fieldValue as string,
      iamUserState?.eisGroups?.groups ?? [],
    ).label;
  }

  return undefined;
};

export const getCashClearingIntegratingApplicationOptions = ({
  rowData,
  options,
  rowDataList,
}: {
  rowData: Record<string, RowDataValueType>;
  options: ISelectableOption[];
  rowDataList: Record<string, RowDataValueType>[];
}): ISelectableOption[] => {
  const transactionType = rowData[ROW_DATA_KEYS.TRANSACTION_TYPE];
  const integratingApplication = rowData[ROW_DATA_KEYS.INTEGRATING_APPLICATION];
  const isNew = isNewRecord(rowData);

  // Find applications already used by other records of the same transaction type.
  // For new or copied records, check all existing records.
  // For existing records, exclude the current record using its ID.
  const existingApplications = rowDataList
    .filter((record) => {
      const isSameTransactionType =
        record[ROW_DATA_KEYS.TRANSACTION_TYPE] === transactionType;

      const isOtherRecord =
        isNew || record[ROW_DATA_KEYS.ID] !== rowData[ROW_DATA_KEYS.ID];

      return isSameTransactionType && isOtherRecord;
    })
    .map((record) => record[ROW_DATA_KEYS.INTEGRATING_APPLICATION]);

  // Exclude applications already used by another record.
  // For existing records, retain the currently selected application.
  // This also handles Global applications, whose value is null.
  const filteredOptions = options.filter(({ value }) => {
    const isAlreadyUsed = existingApplications.includes(value);
    const isCurrentSelection = !isNew && value === integratingApplication;

    return !isAlreadyUsed || isCurrentSelection;
  });

  // Show a disabled placeholder when no applications are available.
  return filteredOptions.length > 0
    ? filteredOptions
    : [{ label: "No applications available", value: "", disabled: true }];
};
